import React, { useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import { SALESPEOPLE } from '../data/salespeople';

// ── Helpers ────────────────────────────────────────────────────────
const toggleInArray = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const summarize = (arr, defaultLabel, singularFmt = (v) => v) => {
  if (!arr || arr.length === 0) return defaultLabel;
  if (arr.length === 1) return singularFmt(arr[0]);
  return `${arr.length} selected`;
};

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [ref, onOutside]);
};

// ── Checkbox row ───────────────────────────────────────────────────
const CheckboxRow = ({ checked, onClick, children, indent = false }) => (
  <div className={`chip-menu-item check-row ${checked ? 'selected' : ''}`} onClick={onClick}>
    <span className={`chip-checkbox ${checked ? 'on' : ''}`} aria-hidden="true">
      {checked && <Icon name="check" size={10} strokeWidth={3} />}
    </span>
    <span className="check-row-label" style={{ paddingLeft: indent ? 4 : 0 }}>
      {children}
    </span>
  </div>
);

// ── Multi-select chip (region / territory / category) ─────────────
const MultiSelectChip = ({ label, icon, defaultLabel, values, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const toggle = (opt) => onChange(toggleInArray(values, opt));
  const clear = (e) => { e.stopPropagation(); onChange([]); };
  const isDefault = !values.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className={`chip ${!isDefault ? 'active' : ''}`} onClick={() => setOpen((o) => !o)}>
        {icon && <Icon name={icon} size={11} />}
        <span className="chip-label">{label}:</span>
        <span className="chip-value">{summarize(values, defaultLabel)}</span>
        <Icon name="chevron-down" size={11} />
      </div>
      {open && (
        <div className="chip-menu chip-menu-multi">
          {!isDefault && (
            <div className="chip-menu-foot">
              <button className="chip-menu-clear" onClick={clear}>Clear</button>
            </div>
          )}
          {options.map((opt) => (
            <CheckboxRow key={opt} checked={values.includes(opt)} onClick={() => toggle(opt)}>
              {opt}
            </CheckboxRow>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Date chip — Year > Month hierarchy with per-year month picks ──
const YEARS  = [2020, 2019, 2018, 2017];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DateChip = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  // value.yearMonths: { [year: number]: string[] }
  //   key present + empty array  → full year selected
  //   key present + populated    → only those months in that year
  const yearMonths = value.yearMonths || {};
  const yearKeys = Object.keys(yearMonths).map(Number);

  const toggleYear = (y) => {
    const next = { ...yearMonths };
    if (y in next) delete next[y]; else next[y] = []; // [] = full year (all 12)
    onChange({ yearMonths: next });
  };
  const toggleMonth = (y, m) => {
    const cur = yearMonths[y];
    let updated;
    if (!cur || cur.length === 0) {
      // year is "full" — deselecting one month means: enumerate to the other 11
      updated = MONTHS.filter((x) => x !== m);
    } else {
      updated = toggleInArray(cur, m);
      // If the user re-checks back to all 12, collapse to [] (full year)
      if (updated.length === MONTHS.length) updated = [];
    }
    onChange({ yearMonths: { ...yearMonths, [y]: updated } });
  };
  const isMonthChecked = (y, m) => {
    const cur = yearMonths[y];
    if (!cur || cur.length === 0) return true; // full year → all months checked
    return cur.includes(m);
  };
  const clear = (e) => { e.stopPropagation(); onChange({ yearMonths: {} }); };

  // Chip display — treat 0-length and 12-length as "full year"
  const monthCount = (y) => {
    const cur = yearMonths[y];
    if (!cur || cur.length === 0) return MONTHS.length; // full
    return cur.length;
  };
  const isFull = (y) => monthCount(y) === MONTHS.length;
  const fullYearCount = yearKeys.filter(isFull).length;
  const partialMonthSum = yearKeys.reduce((acc, y) => acc + (isFull(y) ? 0 : monthCount(y)), 0);

  let display = 'All time';
  if (yearKeys.length === 1) {
    const y = yearKeys[0];
    const c = monthCount(y);
    display =
      c === MONTHS.length ? `CY ${y}` :
      c === 1             ? `${(yearMonths[y][0])} ${y}` :
                            `${c} mo · CY ${y}`;
  } else if (yearKeys.length > 1) {
    if (partialMonthSum === 0) {
      display = `${yearKeys.length} years`;
    } else {
      display = fullYearCount > 0
        ? `${yearKeys.length} years · ${partialMonthSum} mo`
        : `${partialMonthSum} mo across ${yearKeys.length} yrs`;
    }
  }
  const isDefault = yearKeys.length === 0;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className={`chip ${!isDefault ? 'active' : ''}`} onClick={() => setOpen((o) => !o)}>
        <Icon name="calendar" size={11} />
        <span className="chip-label">Date:</span>
        <span className="chip-value">{display}</span>
        <Icon name="chevron-down" size={11} />
      </div>
      {open && (
        <div className="chip-menu chip-menu-multi" style={{ minWidth: 200 }}>
          {!isDefault && (
            <div className="chip-menu-foot">
              <button className="chip-menu-clear" onClick={clear}>Clear all</button>
            </div>
          )}
          {YEARS.map((y) => {
            const isYearSelected = y in yearMonths;
            return (
              <React.Fragment key={y}>
                <CheckboxRow checked={isYearSelected} onClick={() => toggleYear(y)}>
                  CY {y}
                </CheckboxRow>
                {isYearSelected && MONTHS.map((m) => (
                  <CheckboxRow
                    key={`${y}-${m}`}
                    checked={isMonthChecked(y, m)}
                    onClick={() => toggleMonth(y, m)}
                    indent
                  >
                    {m}
                  </CheckboxRow>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Rep chip — multi-select salespeople with avatar/territory ─────
const REP_COLORS = ['#6366F1','#14B8A6','#F59E0B','#EC4899','#0EA5E9','#84CC16','#A855F7','#F43F5E','#10B981','#3B82F6'];
const initials = (n) => n.split(' ').map((p) => p[0]).slice(0, 2).join('');

const RepChip = ({ values, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const toggle = (name) => onChange(toggleInArray(values, name));
  const clear = (e) => { e.stopPropagation(); onChange([]); };
  const isDefault = !values.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className={`chip ${!isDefault ? 'active' : ''}`} onClick={() => setOpen((o) => !o)}>
        <Icon name="people" size={11} />
        <span className="chip-label">Rep:</span>
        <span className="chip-value">{summarize(values, 'All reps')}</span>
        <Icon name="chevron-down" size={11} />
      </div>
      {open && (
        <div className="chip-menu chip-menu-multi" style={{ minWidth: 250, maxHeight: 340, overflowY: 'auto' }}>
          {!isDefault && (
            <div className="chip-menu-foot">
              <button className="chip-menu-clear" onClick={clear}>Clear</button>
            </div>
          )}
          {SALESPEOPLE.map((p, i) => (
            <div
              key={p.name}
              className={`chip-menu-item check-row ${values.includes(p.name) ? 'selected' : ''}`}
              onClick={() => toggle(p.name)}
            >
              <span className={`chip-checkbox ${values.includes(p.name) ? 'on' : ''}`} aria-hidden="true">
                {values.includes(p.name) && <Icon name="check" size={10} strokeWidth={3} />}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: REP_COLORS[i % REP_COLORS.length], color: 'white',
                    display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 600, flexShrink: 0,
                  }}
                >{initials(p.name)}</span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{p.name}</span>
                  <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>{p.territory}</span>
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Filter row ─────────────────────────────────────────────────────
// Default landing state: full CY 2020, no other filters
export const DEFAULT_FILTERS = {
  date:        { yearMonths: { 2020: [] } },
  region:      [],
  territory:   [],
  category:    [],
  salesperson: [],
};

const OPTS = {
  region:    ['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia'],
  territory: ['Northwest', 'Southwest', 'Central', 'Northeast', 'Southeast', 'Canada', 'Europe', 'Pacific'],
  category:  ['Bikes', 'Components', 'Clothing', 'Accessories'],
};

const FilterRow = ({ filters, setFilters }) => (
  <div className="chip-row">
    <DateChip value={filters.date} onChange={(v) => setFilters({ ...filters, date: v })} />
    <MultiSelectChip
      label="Region" icon="globe" defaultLabel="All regions"
      values={filters.region} options={OPTS.region}
      onChange={(v) => setFilters({ ...filters, region: v })}
    />
    <MultiSelectChip
      label="Territory" icon="filter" defaultLabel="All territories"
      values={filters.territory} options={OPTS.territory}
      onChange={(v) => setFilters({ ...filters, territory: v })}
    />
    <MultiSelectChip
      label="Category" icon="box" defaultLabel="All categories"
      values={filters.category} options={OPTS.category}
      onChange={(v) => setFilters({ ...filters, category: v })}
    />
    <RepChip values={filters.salesperson} onChange={(v) => setFilters({ ...filters, salesperson: v })} />
  </div>
);

export default FilterRow;
